import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { useAuth } from "@/contexts/AuthContext";

const CATEGORY_LABEL = {
  auth: "Authentication",
  billing: "Billing & payments",
  sessions: "Sessions & attendance",
  students: "Students",
  settings: "Settings & catalog",
  users: "Users",
  other: "Other",
};

const CATEGORY_DESC = {
  auth: "Login, logout, failed sign-in attempts.",
  billing: "Invoice creation, payments received, refunds.",
  sessions: "Session start/complete, attendance check-in / check-out, assessments.",
  students: "Student create/update, guardian linkage.",
  settings: "Changes to branches, therapy types, templates, payment settings.",
  users: "User create / update / deactivate.",
  other: "Messages, push notifications and anything not in the categories above.",
};

export default function AuditSettingsTab() {
  const { user } = useAuth();
  const isOwner = (user?.roles || []).includes("owner");
  const [enabled, setEnabled] = useState(true);
  const [cats, setCats] = useState({});
  const [knownCats, setKnownCats] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/audit-logs/settings");
      setEnabled(data.enabled);
      setCats(data.categories || {});
      setKnownCats(data.known_categories || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoaded(true); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/audit-logs/settings", { enabled, categories: cats });
      toast.success("Audit settings saved");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const setCat = (k, v) => setCats(c => ({ ...c, [k]: v }));

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base font-display tracking-tight">Audit log settings</CardTitle>
        <p className="text-xs text-muted-foreground mt-1">
          Only the <span className="font-medium text-foreground">Owner</span> can change these. The action of enabling/disabling audit is itself always recorded.
        </p>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Master switch */}
        <div className="flex items-center justify-between p-4 border rounded-md border-border">
          <div>
            <div className="text-sm font-medium">Audit logging is {enabled ? "ON" : "OFF"}</div>
            <div className="text-[11px] text-muted-foreground mt-0.5">
              {enabled
                ? "Every relevant action is being recorded to /api/audit-logs."
                : "No new audit entries are being written. Existing entries are preserved."}
            </div>
          </div>
          <label className="flex items-center gap-2 text-sm">
            <Checkbox
              checked={enabled}
              onCheckedChange={(v) => setEnabled(!!v)}
              disabled={!isOwner || !loaded}
              data-testid="audit-master-toggle"
            />
            <span>{enabled ? "Enabled" : "Disabled"}</span>
          </label>
        </div>

        {/* Per-category toggles */}
        <div>
          <div className="text-sm font-medium mb-2">Categories</div>
          <div className={`space-y-2 ${!enabled ? "opacity-50 pointer-events-none" : ""}`}>
            {knownCats.map((c) => (
              <label
                key={c}
                className={`flex items-start gap-3 p-3 border rounded-md cursor-pointer text-sm ${cats[c] ? "border-primary/40 bg-primary/5" : "border-border"}`}
              >
                <Checkbox
                  checked={!!cats[c]}
                  onCheckedChange={(v) => setCat(c, !!v)}
                  disabled={!isOwner || !loaded || !enabled}
                  data-testid={`audit-category-${c}`}
                />
                <div className="flex-1">
                  <div className="font-medium">{CATEGORY_LABEL[c] || c}</div>
                  <div className="text-[11px] text-muted-foreground">{CATEGORY_DESC[c] || ""}</div>
                </div>
              </label>
            ))}
          </div>
        </div>

        <div className="flex justify-end">
          <Button
            onClick={save}
            disabled={saving || !loaded || !isOwner}
            data-testid="save-audit-settings"
          >
            {saving ? "Saving…" : "Save audit settings"}
          </Button>
        </div>

        {!isOwner && (
          <div className="text-[11px] text-muted-foreground italic pt-2 border-t border-border">
            You&rsquo;re viewing this in read-only mode. Only the Owner role can change audit settings.
          </div>
        )}
      </CardContent>
    </Card>
  );
}
